import React from 'react'

import TextField from '@mui/material/TextField'
import { SxProps } from '@mui/material'

interface NumberFieldProps {
  valueSuffix: string
  numValue: number | null | ''
  minNumValue: number
  maxNumValue: number
  label?: string
  ariaLabel?: string
  disabled?: boolean
  autoFocus?: boolean
  decimalPlaces?: number // allow decimals
  isSmall?: boolean
  errorMessage?: string
  required?: boolean
  placeholder?: string
  onBlur: (newValue: number) => void
  onEnter?: (newValue: number) => void
  sx?: SxProps
}

const NumberField = (props: NumberFieldProps): JSX.Element => {
  const getAdjustedValue = React.useCallback(
    (value: number | null | string): string | null => {
      if (!value || typeof value === 'string') {
        return null
      }
      if (value < props.minNumValue) {
        return props.minNumValue.toString()
      } else if (value > props.maxNumValue) {
        return props.maxNumValue.toString()
      }
      if (props.decimalPlaces) {
        const decimalRoundNumber = Math.pow(10, props.decimalPlaces)
        return (Math.round(parseFloat(`${value || 0}`) * decimalRoundNumber) / decimalRoundNumber).toString()
      }
      return Math.round(value).toString()
    },
    [props.decimalPlaces, props.maxNumValue, props.minNumValue]
  )

  const [numValue, setNumValue] = React.useState<string | null | ''>(getAdjustedValue(props.numValue) || null)

  React.useEffect(() => {
    const newValue = getAdjustedValue(Number(props.numValue))
    setNumValue(newValue)
  }, [props.numValue, getAdjustedValue])

  const getPlaceholder = (): string => {
    const zero = 0
    return `${zero.toFixed(props.decimalPlaces)} ${props.valueSuffix}`
  }

  return (
    <TextField
      size={props.isSmall ? 'small' : undefined}
      type="number"
      variant="outlined"
      label={props.label}
      placeholder={props.placeholder ? props.placeholder : getPlaceholder()}
      value={numValue}
      disabled={props.disabled}
      autoFocus={props.autoFocus}
      required={props.required}
      InputProps={{
        endAdornment: props.valueSuffix,
      }}
      inputProps={{
        step: props.decimalPlaces ? 1 / Math.pow(10, props.decimalPlaces) : 1,
        min: props.minNumValue,
        max: props.maxNumValue,
        'aria-label': props.ariaLabel,
      }}
      error={!!props.errorMessage}
      helperText={props.errorMessage}
      onKeyPress={(event: React.KeyboardEvent<HTMLInputElement>): void => {
        if (event.key === 'Enter' && props.onEnter) {
          const newValue = parseFloat((event.target as HTMLInputElement).value) || 0
          const adjustedValue = getAdjustedValue(newValue)
          setNumValue(adjustedValue)
          props.onEnter(Number(adjustedValue))
        }
      }}
      onBlur={(event): void => {
        const newValue = parseFloat((event.target as HTMLInputElement).value) || 0
        const adjustedValue = getAdjustedValue(newValue)
        setNumValue(adjustedValue || '')
        props.onBlur(Number(adjustedValue))
      }}
      onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
        setNumValue(event.target.value)
      }}
      sx={{ ...props.sx }}
    />
  )
}

export default NumberField
